import GeneralForm from '@/components/pages/create-webinar/config/GeneralForm';
import DiffusionForm from '@/components/pages/create-webinar/config/DiffusionForm';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
// import SessionForm from '../../create-webinar/config/SessionForm';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Title from '@/components/ui/Title';
import type { Webinar } from '@/features/webinar/types';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import ModeratorsForm from '../../create-webinar/config/ModeratorForm';
import SigninSection from '@/pages/create-webinar/SigninSection';
import LiveSection from '@/pages/create-webinar/LiveSection';
import ReplaySection from '@/pages/create-webinar/ReplaySection';
import ThankYouSection from '@/pages/create-webinar/ThankYouSection';
import IntegrationSection from '@/pages/create-webinar/IntegrationSection';
import { listWebhooks, updateWebinar } from '@/features/webinar/webinarThunks';
import type { WebinarLanguage } from '@/features/webinar/types';
import { useEffect, useState, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'react-toastify';
import { LanguageFlag } from '@/components/ui/FlagIcon';
import { APP_LANGUAGES } from '@/i18n/languages';
import WaitingRoomSection from '@/pages/create-webinar/WaitingRoomSection';
import WebinarSessionForm from '../../create-webinar/config/WebinarSessionForm';
import PresenterForm from '../../create-webinar/config/PresenterForm';
import EvergreenSection from '@/pages/create-webinar/EvergreenSection';
import ChatEvergreenSection from '@/pages/create-webinar/ChatEvergreenSection';
import InteractionSection from '@/pages/create-webinar/InteractionSection';
import SpeakersForm from '../../create-webinar/config/SpeakersForm';
import { useSearchParams } from 'react-router-dom';

// Liste blanche des onglets adressables par `?tab=` : une valeur inconnue afficherait une page vide.
const LINKABLE_TABS = [
  'config',
  'inscription',
  'waiting-room',
  'live',
  'evergreen',
  'chat-evergreen',
  'interactions',
  'thank-you',
  'replay',
  'integration',
];

interface EditWebinarProps {
  webinar: Webinar | null;
  onSessionsChanged?: (options?: {
    excludedSessionId?: number;
    realignWithNextSession?: boolean;
  }) => void | Promise<void>;
}

export default function EditWebinar({ webinar, onSessionsChanged }: EditWebinarProps) {
  const { t } = useTranslation('createWebinar');
  const { currentSessionConfiguration, presenters, moderators, currentWebinarSession, speakers } =
    useAppSelector((state) => state.webinar);
  const sessionType = currentWebinarSession?.sessionType;
  const isEvergreen = sessionType === 'EVERGREEN';
  const isYouTube = webinar?.transmissionMode === 'YOUTUBE';
  const hidePresentersSection = isEvergreen || currentSessionConfiguration?.type === 'EVERGREEN';
  const hideSpeakersSection =
    isEvergreen || currentSessionConfiguration?.type === 'EVERGREEN' || isYouTube;
  const youtubeTransmissionAllowed = useAppSelector(
    (state) => state.billing.usage.data?.youtubeTransmissionAllowed ?? false,
  );
  // Section Diffusion réservée aux plans éligibles (ou webinaire déjà en YouTube), masquée en evergreen.
  const showDiffusionSection = (youtubeTransmissionAllowed || isYouTube) && !hidePresentersSection;

  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const webinarId = webinar?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = searchParams.get('tab');
    return requestedTab && LINKABLE_TABS.includes(requestedTab) ? requestedTab : 'config';
  });
  const [accordionValue, setAccordionValue] = useState<string[]>([]);

  // L'initialisation ci-dessus couvre les remontages de la page ; cet effet couvre le cas
  // inverse, un lien profond reçu alors que la fiche est déjà ouverte et ne remonte pas.
  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (!requestedTab || !LINKABLE_TABS.includes(requestedTab)) return;
    setActiveTab(requestedTab);
  }, [searchParams]);

  // La page se remonte au fil des chargements : les paramètres de lien profond doivent
  // survivre, sinon l'onglet retombe sur Configuration. On ne les retire qu'au premier
  // changement d'onglet fait à la main.
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (!searchParams.has('tab') && !searchParams.has('focus')) return;
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('tab');
    nextSearchParams.delete('focus');
    setSearchParams(nextSearchParams, { replace: true });
  };

  useEffect(() => {
    if (workspaceId && webinarId && !isNaN(webinarId)) {
      dispatch(listWebhooks({ workspaceId, webinarId }));
    }
  }, [workspaceId, webinarId, dispatch]);

  useEffect(() => {
    if (isEvergreen && activeTab === 'live') {
      setActiveTab('evergreen');
    } else if (!isEvergreen && (activeTab === 'evergreen' || activeTab === 'chat-evergreen')) {
      setActiveTab('thank-you');
    }
  }, [isEvergreen, activeTab]);

  const webinarPresenters = presenters.filter((s) => webinar?.id && s.webinarId === webinar.id);
  const webinarModerators = moderators.filter((m) => webinar?.id && m.webinarId === webinar.id);
  const webinarSpeakers = speakers.filter((s) => webinar?.id && s.webinarId === webinar.id);
  const hasPresenters = webinarPresenters.length > 0;
  const hasModerators = webinarModerators.length > 0;
  const hasSpeakers = webinarSpeakers.length > 0;

  // Général → Diffusion (quand la section existe) → Présentateurs, comme en création.
  const openAccordionAfterDiffusionSave = useCallback(() => {
    setAccordionValue(hidePresentersSection ? ['item-4'] : ['item-3']);
  }, [hidePresentersSection]);

  const openAccordionAfterGeneralSave = useCallback(() => {
    if (showDiffusionSection) {
      setAccordionValue(['item-diffusion']);
      return;
    }
    openAccordionAfterDiffusionSave();
  }, [showDiffusionSection, openAccordionAfterDiffusionSave]);

  // Modérateur sauvegardé → ouvre Invité ; en evergreen (section Invité masquée) Modérateur est la
  // dernière sous-section, on passe à l'onglet Inscription en remontant en haut.
  const openSectionAfterModeratorSave = useCallback(() => {
    if (hideSpeakersSection) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveTab('inscription');
      return;
    }
    setAccordionValue(['item-5']);
  }, [hideSpeakersSection]);

  // Invité sauvegardé → dernière sous-section, on passe à l'onglet Inscription en remontant en haut.
  const goToInscriptionAfterSpeakersSave = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveTab('inscription');
  }, []);

  const handleLanguageChange = useCallback(
    async (language: WebinarLanguage) => {
      if (!workspaceId || !webinar?.id) return;
      const result = await dispatch(
        updateWebinar({
          workspaceId,
          id: webinar.id,
          input: {
            title: webinar.title,
            shortDescription: webinar.shortDescription ?? '',
            description: webinar.description ?? '',
            language,
          },
        }),
      );
      if (updateWebinar.fulfilled.match(result)) {
        toast.success(t('configuration.languageSaved'));
      } else {
        toast.error(t('configuration.languageSaveError'));
      }
    },
    [workspaceId, webinar, dispatch, t],
  );

  useEffect(() => {
    if (!hidePresentersSection) return;
    setAccordionValue((prev) =>
      prev.includes('item-3') ? prev.filter((v) => v !== 'item-3') : prev,
    );
  }, [hidePresentersSection]);

  useEffect(() => {
    if (!hideSpeakersSection) return;
    setAccordionValue((prev) =>
      prev.includes('item-5') ? prev.filter((v) => v !== 'item-5') : prev,
    );
  }, [hideSpeakersSection]);

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange}>
      <TabsList className="w-full pb-12">
        <TabsTrigger value="config">
          <Title level={3}>{t('tabs.configuration')}</Title>
        </TabsTrigger>
        <TabsTrigger value="inscription">
          <Title level={3}>{t('tabs.inscription')}</Title>
        </TabsTrigger>

        <TabsTrigger value="waiting-room">
          <Title level={3}>{t('tabs.waitingRoom')}</Title>
        </TabsTrigger>

        {!isEvergreen && (
          <TabsTrigger value="live">
            <Title level={3}>{t('tabs.live')}</Title>
          </TabsTrigger>
        )}

        {isEvergreen && (
          <>
            <TabsTrigger value="evergreen">
              <Title level={3}>{t('tabs.evergreen')}</Title>
            </TabsTrigger>
            <TabsTrigger value="chat-evergreen">
              <Title level={3}>{t('tabs.chatEvergreen')}</Title>
            </TabsTrigger>
          </>
        )}

        {!isEvergreen && (
          <TabsTrigger value="interactions">
            <Title level={3}>{t('tabs.interactions')}</Title>
          </TabsTrigger>
        )}

        <TabsTrigger value="thank-you">
          <Title level={3}>{t('tabs.thankYou')}</Title>
        </TabsTrigger>

        <TabsTrigger value="replay">
          <Title level={3}>{t('tabs.replay')}</Title>
        </TabsTrigger>

        <TabsTrigger value="integration">
          <Title level={3}>{t('tabs.integrations')}</Title>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="config">
        <Accordion
          type="multiple"
          className="w-full"
          value={accordionValue}
          onValueChange={(value) => setAccordionValue(value || [])}
        >
          <div className="flex flex-col gap-8">
            <div className="space-y-1">
              <Label htmlFor="webinar-language" className="text-sm font-medium">
                {t('generalForm.language')}
              </Label>
              <p className="text-sm text-secondary">{t('generalForm.languageDescription')}</p>
              <Select
                value={webinar?.language ?? ''}
                onValueChange={(v) => void handleLanguageChange(v as WebinarLanguage)}
                disabled={!webinar}
              >
                <SelectTrigger id="webinar-language" className="w-full mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APP_LANGUAGES.map(({ code, apiValue }) => (
                    <SelectItem key={code} value={apiValue}>
                      <span className="flex items-center gap-2">
                        <LanguageFlag code={code} className="w-5 h-3.5 rounded-sm shrink-0" />
                        {t(`generalForm.languages.${code}`)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <AccordionItem value="item-1" className="accordion-page" hideSeparator>
              <AccordionTrigger
                disabled={!webinar}
                aria-disabled={!webinar}
                fillForm={!!currentSessionConfiguration}
              >
                {t('configuration.session')}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 text-balance">
                <WebinarSessionForm
                  webinar={webinar}
                  isEdit={true}
                  onSuccess={() => setAccordionValue(['item-2'])}
                  onSessionsChanged={onSessionsChanged}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="accordion-page" hideSeparator>
              <AccordionTrigger fillForm={!!webinar}>{t('configuration.general')}</AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 text-balance">
                <GeneralForm webinar={webinar} onSuccess={openAccordionAfterGeneralSave} />
              </AccordionContent>
            </AccordionItem>

            {showDiffusionSection && (
              <AccordionItem value="item-diffusion" className="accordion-page" hideSeparator>
                <AccordionTrigger disabled={!webinar} aria-disabled={!webinar} fillForm={!!webinar}>
                  {t('configuration.diffusion')}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-balance">
                  <DiffusionForm webinar={webinar} onSuccess={openAccordionAfterDiffusionSave} />
                </AccordionContent>
              </AccordionItem>
            )}

            {!hidePresentersSection && (
              <AccordionItem value="item-3" className="accordion-page" hideSeparator>
                <AccordionTrigger
                  disabled={!webinar}
                  aria-disabled={!webinar}
                  fillForm={hasPresenters}
                >
                  {t('configuration.presenters')}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-balance">
                  <PresenterForm
                    webinar={webinar}
                    isEdit={true}
                    onSuccess={() => setAccordionValue(['item-4'])}
                  />
                </AccordionContent>
              </AccordionItem>
            )}

            <AccordionItem value="item-4" className="accordion-page" hideSeparator>
              <AccordionTrigger
                disabled={!webinar}
                aria-disabled={!webinar}
                fillForm={hasModerators}
              >
                {t('configuration.moderators')}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 text-balance">
                <ModeratorsForm
                  webinar={webinar}
                  isEdit={true}
                  onSaveSuccess={openSectionAfterModeratorSave}
                />
              </AccordionContent>
            </AccordionItem>
            {!hideSpeakersSection && (
              <AccordionItem value="item-5" className="accordion-page" hideSeparator>
                <AccordionTrigger
                  disabled={!webinar}
                  aria-disabled={!webinar}
                  fillForm={hasSpeakers}
                >
                  {t('configuration.speakers')}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-balance">
                  <SpeakersForm webinar={webinar} onSuccess={goToInscriptionAfterSpeakersSave} />
                </AccordionContent>
              </AccordionItem>
            )}
          </div>
        </Accordion>
      </TabsContent>
      <TabsContent value="inscription">
        <SigninSection
          currentWebinarId={webinar?.id}
          isEdit={true}
          onSaveSuccess={() => setActiveTab('waiting-room')}
        />
      </TabsContent>
      <TabsContent value="waiting-room">
        <WaitingRoomSection
          currentWebinarId={webinar?.id}
          isEdit={true}
          onSaveSuccess={() => setActiveTab(isEvergreen ? 'evergreen' : 'live')}
        />
      </TabsContent>
      {!isEvergreen && (
        <TabsContent value="live">
          <LiveSection
            currentWebinarId={webinar?.id}
            isEdit={true}
            onSaveSuccess={() => setActiveTab('interactions')}
          />
        </TabsContent>
      )}
      <TabsContent value="evergreen">
        <EvergreenSection
          currentWebinarId={webinar?.id}
          isEdit={true}
          onSaveSuccess={() => setActiveTab('chat-evergreen')}
        />
      </TabsContent>

      <TabsContent value="chat-evergreen">
        <ChatEvergreenSection
          currentWebinarId={webinar?.id}
          isEdit={true}
          onSaveSuccess={() => setActiveTab('thank-you')}
        />
      </TabsContent>

      <TabsContent value="interactions">
        <InteractionSection currentWebinarId={webinar?.id} isEdit={true} />
      </TabsContent>

      <TabsContent value="thank-you">
        <ThankYouSection
          currentWebinarId={webinar?.id}
          isEdit={true}
          onSaveSuccess={() => setActiveTab('replay')}
        />
      </TabsContent>
      <TabsContent value="replay">
        <ReplaySection
          currentWebinarId={webinar?.id}
          isEdit={true}
          onSaveSuccess={() => setActiveTab('integration')}
        />
      </TabsContent>
      <TabsContent value="integration">
        <IntegrationSection currentWebinarId={webinar?.id} isEdit={true} />
      </TabsContent>
    </Tabs>
  );
}
